import { memo, MouseEvent, useCallback, useMemo } from 'react';

import {
  DeleteFilled,
  EditFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { message, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { History } from 'history';
import { Link } from 'react-router-dom';

import { ApiError, handleError } from '../../../../api/base';
import { useAuth } from '../../../../hooks/useAuth';
import { RoleModel } from '../../../../models/role';
import { UserModel } from '../../../../models/user';
import { useAppDispatch } from '../../../../store';
import { deleteUser } from '../../../../store/features/users/usersSlice';
import { formatDate, propsAreEqual, UserPermissions } from '../../../../util';
import { DrawerHashRoute } from '../../../containers/Drawers/types';

const { Text } = Typography;

interface ClientUsersTableProps {
  users: UserModel[];
  loading: boolean;
  history: History;
  page: number;
  pageSize: number;
  total: number;
  onPaginate: (page: number, pageSize: number, sort?: string) => void;
}

const ClientUsersTable = ({
  users,
  loading,
  history,
  page,
  pageSize,
  total,
  onPaginate,
}: ClientUsersTableProps) => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();

  const onError = useCallback((err: ApiError) => {
    message.destroy('deleting-user-message');
    handleError(err);
  }, []);

  const handleDeleteUser = useCallback(
    async (user: UserModel) => {
      message.loading(
        { content: 'Deleting user...', key: 'deleting-user-message' },
        0
      );

      const res: any = await dispatch(deleteUser(user.uuid));
      if (res.error) {
        onError({ error: res.payload.error });
      } else {
        message.destroy('deleting-user-message');
        message.success(`User '${user.name}' deleted.`);
      }
    },
    [onError, dispatch]
  );

  const handleConfirmDeleteUser = useCallback(
    (e: MouseEvent, user: UserModel) => {
      e.preventDefault();
      Modal.confirm({
        title: `Delete user '${user.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover this user.',
        okText: 'Delete',
        onOk: () => handleDeleteUser(user),
      });
    },
    [handleDeleteUser]
  );

  const actionsColumn: any = useMemo(
    () => ({
      title: 'Actions',
      key: 'actions',
      dataIndex: 'uuid',
      align: 'right',
      render: (_: string, user: UserModel) => {
        return (
          <Space direction="horizontal" size="middle">
            {hasPermission(UserPermissions.UsersEdit) && (
              <Tooltip title="Edit">
                <EditFilled
                  onClick={() =>
                    history.push({
                      hash: DrawerHashRoute.UserForm,
                      state: { data: user },
                    })
                  }
                  className="client-users-table-list-action-icon"
                />
              </Tooltip>
            )}
            {hasPermission(UserPermissions.UsersDelete) && (
              <Tooltip title="Delete">
                <DeleteFilled
                  onClick={(e) => handleConfirmDeleteUser(e, user)}
                  className="client-users-table-list-action-icon"
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    }),
    [hasPermission, handleConfirmDeleteUser, history]
  );

  const columns: any = useMemo(
    () => [
      {
        title: 'No.',
        dataIndex: 'index',
        key: 'index',
        width: 50,
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Name',
        width: 160,
        render: (user: UserModel) => (
          <Link to={`/users/${user.uuid}`} className="ant-btn-link">
            {user.name}
          </Link>
        ),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 250,
        render: (email: string) =>
          !!email ? (
            <Tooltip title={email} mouseEnterDelay={1}>
              <Text ellipsis style={{ maxWidth: '400px' }}>
                {email}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              No email.
            </Text>
          ),
      },
      {
        title: 'Roles',
        dataIndex: 'roles',
        key: 'roles',
        width: 200,
        render: (roles: RoleModel[]) =>
          roles?.map((role) =>
            role?.display_name ? (
              <Tag key={role.id || role.display_name} color="purple">
                {role.display_name}
              </Tag>
            ) : null
          ),
      },
      {
        title: 'Last updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 200,
        sorter: true,
        render: (date: string) => {
          return (
            <div>
              {date ? formatDate(date, undefined, 'America/Chicago') : null}
            </div>
          );
        },
      },
      actionsColumn,
    ],
    [actionsColumn, history]
  );

  return (
    <>
      <Table
        loading={loading}
        locale={{ emptyText: <></> }}
        rowKey={(user) => `${user.uuid}-row-key`}
        className="client-users-table-list"
        sortDirections={['ascend', 'descend', 'ascend']}
        onChange={(pagination, _filters, sorter) => {
          const primarySorter = Array.isArray(sorter) ? sorter[0] : sorter;
          const order =
            primarySorter?.order === 'ascend'
              ? 'asc'
              : primarySorter?.order === 'descend'
              ? 'desc'
              : undefined;
          const sortParam = order ? `updated_at:${order}` : undefined;
          onPaginate(
            pagination.current || page,
            pagination.pageSize || pageSize,
            sortParam
          );
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        columns={columns}
        dataSource={users}
      />
    </>
  );
};

export default memo(ClientUsersTable, propsAreEqual);
